/* eslint-disable camelcase */

import { useQueryClient } from '@tanstack/react-query'
import PropTypes from 'prop-types'
import { createContext, useEffect, useMemo, useReducer, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'
import { useLocation, useNavigate } from 'react-router-dom'
import * as endPoints from '@/endpoints'
import * as allauth from '@/features/allauth/lib/allauth'
import { needsMfaSetup } from '@/features/allauth/lib/mfaPolicy'
import axios from '@/lib/axios'
import { setPseudonymousUser } from '@/lib/faro'
import { changeMapStyle, onChangeViewport } from '@/slices/map'

const ACTIVE_ORG_STORAGE_KEY = 'power.activeOrg'
const TENANT_QUERY_KEYS = new Set([
    'onboarding',
    'locations',
    'locationSuggestions',
    'powerMeters',
    'users',
    'currentOrg',
    'orgHolidays',
    'powerHolidays',
    'holidayMeterData',
    'preHolidayMeterData',
    'powerOverview',
    'exploreData',
    'heatmapData',
    'rawValuesData',
    'meterData',
    'power',
    'user-detail',
    'user-block-status',
    'user-followers',
    'user-following',
    'user-photos',
    'blocked-users',
    'xmpp-dm-list',
    'xmpp-dm-room'
])

function readStoredActiveOrg() {
    if (typeof window === 'undefined') {
        return null
    }

    try {
        const storedOrg = window.localStorage.getItem(ACTIVE_ORG_STORAGE_KEY)
        return storedOrg ? JSON.parse(storedOrg) : null
    } catch {
        return null
    }
}

function persistActiveOrg(org) {
    if (typeof window === 'undefined') {
        return
    }

    try {
        if (org?.id) {
            window.localStorage.setItem(
                ACTIVE_ORG_STORAGE_KEY,
                JSON.stringify(org)
            )
        } else {
            window.localStorage.removeItem(ACTIVE_ORG_STORAGE_KEY)
        }
    } catch {
        // Storage persistence is only for display/bootstrap state.
    }
}

// ── Initial state (matches SessionAuthContext) ─────────────────────────
const initialState = {
    isAuthenticated: false,
    isInitialized: false,
    searchBootstrap: null,
    user: null,
    activeOrg: readStoredActiveOrg(),
    activeOrgLoaded: false,
    activeOrgError: null,
    isSwitchingActiveOrg: false,
    settings: {
        style: {
            colorPreset: 'site',
            containerStretch: true,
            contrast: 'high',
            direction: 'ltr',
            layout: 'vertical',
            mapStyle: 'osmStyle',
            navColor: 'evident',
            responsiveFontSizes: true,
            roundedCorners: true,
            theme: 'black'
        },
        user: {
            language: 'en'
        }
    }
}

const SEARCH_BOOTSTRAP_REFRESH_LEEWAY_SECONDS = 60

// ── Settings updater (same as SessionAuthContext) ──────────────────────
function updateSettingsJson({ settings, keys, value }) {
    const depthWeWant = keys.length

    function updateJson(obj, listOfKeys, newValue, depth = 1) {
        if (depth === depthWeWant) {
            obj[listOfKeys[depth - 1]] = newValue
        } else if (obj instanceof Object) {
            Object.entries(obj).forEach(([k, v]) => {
                if (k !== listOfKeys[depth - 1]) {
                    // skip
                } else if (v instanceof Object) {
                    updateJson(v, listOfKeys, newValue, depth + 1)
                } else if (k === String([listOfKeys[depth - 1]])) {
                    obj[k] = newValue
                }
            })
        }
    }
    updateJson(settings, keys, value)
    return settings
}

// ── Reducer ────────────────────────────────────────────────────────────
const handlers = {
    INITIALIZE: (state, action) => {
        const { isAuthenticated, searchBootstrap = null, user } = action.payload
        if (user !== null) {
            const { settings, ...userWithoutSettings } = user
            return {
                ...state,
                isAuthenticated,
                isInitialized: true,
                searchBootstrap,
                user: userWithoutSettings,
                settings
            }
        }
        return {
            ...state,
            isAuthenticated,
            isInitialized: true,
            searchBootstrap,
            user,
            activeOrg: null,
            activeOrgLoaded: true,
            activeOrgError: null,
            isSwitchingActiveOrg: false
        }
    },
    LOGIN: (state, action) => {
        const { searchBootstrap = null, user } = action.payload
        const { settings, ...userWithoutSettings } = user
        return {
            ...state,
            isAuthenticated: true,
            searchBootstrap,
            user: userWithoutSettings,
            settings
        }
    },
    LOGOUT: (state, action) => ({
        ...state,
        isAuthenticated: false,
        searchBootstrap: action.payload?.searchBootstrap ?? null,
        user: null,
        activeOrg: null,
        activeOrgLoaded: true,
        activeOrgError: null,
        isSwitchingActiveOrg: false,
        settings: { style: state.settings.style }
    }),
    REGISTER: (state, action) => {
        const { searchBootstrap = null, user } = action.payload
        const { settings, ...userWithoutSettings } = user
        return {
            ...state,
            isAuthenticated: true,
            searchBootstrap,
            user: userWithoutSettings,
            settings
        }
    },
    UPDATE_USER: (state, action) => {
        const { user } = action.payload
        const { settings: _settings, ...userWithoutSettings } = user
        return {
            ...state,
            user: userWithoutSettings
        }
    },
    UPDATE_SETTINGS: (state, action) => ({
        ...state,
        settings: updateSettingsJson({
            settings: state.settings,
            keys: action.payload.attribute,
            value: action.payload.value
        })
    }),
    SET_ACTIVE_ORG: (state, action) => ({
        ...state,
        activeOrg: action.payload.org,
        activeOrgLoaded: true,
        activeOrgError: action.payload.error ?? null
    }),
    SET_ACTIVE_ORG_SWITCHING: (state, action) => ({
        ...state,
        isSwitchingActiveOrg: action.payload.isSwitching
    })
}

const reducer = (state, action) =>
    handlers[action.type] ? handlers[action.type](state, action) : state

// ── Context ────────────────────────────────────────────────────────────
const AuthContext = createContext({
    ...initialState,
    platform: 'Allauth',
    login: () => Promise.resolve(),
    logout: () => Promise.resolve(),
    refreshSession: () => Promise.resolve(),
    register: () => Promise.resolve(),
    switchActiveOrg: () => Promise.resolve()
})

// ── Cookie helper ──────────────────────────────────────────────────────
export function getCookie(name) {
    let cookieValue = null
    if (document.cookie && document.cookie !== '') {
        const cookies = document.cookie.split(';')
        for (let i = 0; i < cookies.length; i++) {
            const cookie = cookies[i].trim()
            if (cookie.substring(0, name.length + 1) === `${name}=`) {
                cookieValue = decodeURIComponent(
                    cookie.substring(name.length + 1)
                )
                break
            }
        }
    }
    return cookieValue
}

// ── Extract user from allauth session response ────────────────────────
function extractUser(result) {
    // The HeadlessAdapter.serialize_user() returns the enriched user data
    // inside data.user. The user includes the settings field from the model.
    if (result?.data?.user) {
        return result.data.user
    }
    return null
}

function extractSearchBootstrap(result) {
    if (result?.data?.search_bootstrap) {
        return result.data.search_bootstrap
    }
    return null
}

// ── Provider ───────────────────────────────────────────────────────────
export const AuthProvider = (props) => {
    const { children } = props
    const { i18n } = useTranslation()
    const [state, dispatch] = useReducer(reducer, initialState)
    const sessionRefreshPromiseRef = useRef(null)
    const location = useLocation()
    const navigate = useNavigate()
    const reduxDispatch = useDispatch()
    const queryClient = useQueryClient()

    const setActiveOrgState = (org, error = null) => {
        persistActiveOrg(org)
        dispatch({
            type: 'SET_ACTIVE_ORG',
            payload: { org, error }
        })
    }

    const clearActiveOrgState = (error = null) => {
        setActiveOrgState(null, error)
    }

    const loadActiveOrg = async () => {
        try {
            const response = await axios.get(endPoints.ORG_ACTIVE)
            const activeOrg = response.data?.org ?? null
            setActiveOrgState(activeOrg)
            return activeOrg
        } catch (error) {
            clearActiveOrgState(error)
            throw error
        }
    }

    const invalidateTenantQueries = () =>
        queryClient.invalidateQueries({
            predicate: (query) => TENANT_QUERY_KEYS.has(query.queryKey?.[0])
        })

    // Helper function to setup authenticated user state (same as SessionAuthContext)
    const setupAuthenticatedUser = (user, searchBootstrap = null) => {
        if (user) {
            // Set fresh CSRF token
            axios.defaults.headers.common['X-CSRFToken'] =
                getCookie('csrftoken')

            // Wire Faro user context. telemetryId is the raw user UUID for
            // staff and an empty string for pupils — pupils stay unattributed.
            setPseudonymousUser(user.telemetryId ?? '')

            // Language setup
            const newLanguage = user.settings?.user?.language
            if (newLanguage && newLanguage !== 'en') {
                i18n.changeLanguage(newLanguage)
            }

            // Redux map setup
            if (user.settings?.map?.viewport) {
                reduxDispatch(onChangeViewport(user.settings.map.viewport))
            }

            if (user.settings?.style?.mapStyle === undefined)
                reduxDispatch(changeMapStyle({ styleName: 'osmStyle' }))
            else
                reduxDispatch(
                    changeMapStyle({ styleName: user.settings.style.mapStyle })
                )

            // Dispatch INITIALIZE action
            dispatch({
                type: 'INITIALIZE',
                payload: {
                    isAuthenticated: true,
                    searchBootstrap,
                    user
                }
            })
        }
    }

    const setupGuestSession = (
        searchBootstrap,
        { initialize = false } = {}
    ) => {
        persistActiveOrg(null)
        dispatch({
            type: initialize ? 'INITIALIZE' : 'LOGOUT',
            payload: {
                isAuthenticated: false,
                searchBootstrap,
                user: null
            }
        })
    }

    const applySessionResult = async (result, { initialize = false } = {}) => {
        const user = extractUser(result)
        const searchBootstrap = extractSearchBootstrap(result)

        axios.defaults.headers.common['X-CSRFToken'] = getCookie('csrftoken')

        if (user) {
            setupAuthenticatedUser(user, searchBootstrap)
            // The backend refuses org calls until an admin who must use two-factor
            // authentication has set it up, so do not ask yet.
            if (!needsMfaSetup(user)) {
                await loadActiveOrg().catch(() => undefined)
            }
        } else {
            setupGuestSession(searchBootstrap, { initialize })
        }

        return result
    }

    const refreshSession = async ({ initialize = false } = {}) => {
        if (sessionRefreshPromiseRef.current) {
            return sessionRefreshPromiseRef.current
        }

        const refreshPromise = allauth
            .getSession()
            .then((result) => applySessionResult(result, { initialize }))
            .catch((err) => {
                if (initialize) {
                    axios.defaults.headers.common['X-CSRFToken'] =
                        getCookie('csrftoken')
                    setupGuestSession(null, { initialize: true })
                }
                throw err
            })
            .finally(() => {
                sessionRefreshPromiseRef.current = null
            })

        sessionRefreshPromiseRef.current = refreshPromise
        return refreshPromise
    }

    const switchActiveOrg = async (orgId) => {
        dispatch({
            type: 'SET_ACTIVE_ORG_SWITCHING',
            payload: { isSwitching: true }
        })

        try {
            const response = await axios.post(endPoints.ORG_ACTIVE, {
                org: orgId
            })
            const activeOrg = response.data?.org ?? null
            setActiveOrgState(activeOrg)
            await invalidateTenantQueries()
            return activeOrg
        } finally {
            dispatch({
                type: 'SET_ACTIVE_ORG_SWITCHING',
                payload: { isSwitching: false }
            })
        }
    }

    // Initialize: call getSession to check if already authenticated
    useEffect(() => {
        const initialize = async () => {
            try {
                await refreshSession({ initialize: true })
            } catch {
                /* best effort */
            }
        }

        initialize()
    }, [])

    useEffect(() => {
        if (!state.isInitialized || !state.searchBootstrap?.expires_at) {
            return undefined
        }

        const refreshDelayMs =
            state.searchBootstrap.expires_at * 1000 -
            Date.now() -
            SEARCH_BOOTSTRAP_REFRESH_LEEWAY_SECONDS * 1000

        if (refreshDelayMs <= 0) {
            refreshSession().catch(() => undefined)
            return undefined
        }

        const timeoutId = window.setTimeout(() => {
            refreshSession().catch(() => undefined)
        }, refreshDelayMs)

        return () => window.clearTimeout(timeoutId)
    }, [state.isInitialized, state.searchBootstrap?.expires_at])

    useEffect(() => {
        if (!state.isInitialized) {
            return undefined
        }

        const handleForeground = () => {
            const expiresAt = state.searchBootstrap?.expires_at
            if (
                typeof expiresAt === 'number' &&
                expiresAt - SEARCH_BOOTSTRAP_REFRESH_LEEWAY_SECONDS <=
                    Date.now() / 1000
            ) {
                refreshSession().catch(() => undefined)
            }
        }

        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible') {
                handleForeground()
            }
        }

        window.addEventListener('focus', handleForeground)
        document.addEventListener('visibilitychange', handleVisibilityChange)

        return () => {
            window.removeEventListener('focus', handleForeground)
            document.removeEventListener(
                'visibilitychange',
                handleVisibilityChange
            )
        }
    }, [state.isInitialized, state.searchBootstrap?.expires_at])

    // Listen for auth change events from the allauth library
    useEffect(() => {
        return allauth.onAuthChange((result) => {
            const user = extractUser(result)
            const searchBootstrap = extractSearchBootstrap(result)
            if (user) {
                setupAuthenticatedUser(user, searchBootstrap)
                if (!needsMfaSetup(user)) {
                    loadActiveOrg().catch(() => undefined)
                }
            } else if (state.isAuthenticated) {
                setupGuestSession(searchBootstrap)
            }
        })
    }, [state.isAuthenticated])

    const login = async (email, password) => {
        const result = await allauth.login({ email, password })

        // Check for pending flows (e.g., MFA, email verification)
        const pendingFlow = allauth.getPendingFlow(result)
        if (pendingFlow) {
            const pendingUrl = allauth.pendingFlowUrl(pendingFlow)
            if (pendingUrl) {
                navigate(pendingUrl)
            }
            return result
        }

        // Successful login — get fresh session with enriched user
        const session = await refreshSession()
        const user = extractUser(session)

        if (user) {
            setupAuthenticatedUser(user, extractSearchBootstrap(session))
        }

        if (location.pathname === '/login') {
            navigate('/')
        }

        return result
    }

    const logout = async () => {
        try {
            const result = await allauth.logout()
            setupGuestSession(extractSearchBootstrap(result))
            navigate('/')
        } catch (err) {
            const is401 =
                err?.response?.status === 401 ||
                err?.status === 401 ||
                (err?.message && err.message.includes('401'))

            if (is401) {
                setupGuestSession(null)
                navigate('/')
            } else {
                throw err
            }
        }
    }

    const register = async (
        email,
        username,
        password1,
        password2,
        turnstileToken
    ) => {
        const result = await allauth.signup({
            email,
            username,
            password: password1,
            turnstileToken
        })

        return result
    }

    const resendVerificationEmail = async (email) =>
        allauth.resendVerificationEmail({ email })

    const resetPassword = async (email, turnstileToken) =>
        allauth.requestPasswordReset({ email, turnstileToken })

    const resetPasswordConfirm = async (password, key) => {
        const result = await allauth.resetPassword({ password, key })

        // After password reset, user may be logged in automatically
        try {
            const session = await refreshSession()
            const user = extractUser(session)
            if (user) {
                setupAuthenticatedUser(user, extractSearchBootstrap(session))
            }
        } catch {
            // Not authenticated after reset — that's fine
        }

        return result
    }

    const passwordChange = async (current_password, new_password) =>
        allauth.changePassword({ current_password, new_password })

    const updateUser = async (user) => {
        dispatch({
            type: 'UPDATE_USER',
            payload: { user }
        })
    }

    const updateSettings = (updatedSettings) => {
        dispatch({ type: 'UPDATE_SETTINGS', payload: updatedSettings })

        axios
            .put(`${endPoints.UPDATE_SETTINGS}`, updatedSettings)
            .catch((error) => {
                console.log('save settings error', error)
            })
    }

    const contextValue = useMemo(
        () => ({
            ...state,
            platform: 'Allauth',
            login,
            logout,
            refreshSession,
            register,
            resendVerificationEmail,
            resetPassword,
            resetPasswordConfirm,
            passwordChange,
            updateUser,
            updateSettings,
            switchActiveOrg
        }),
        [state]
    )

    return (
        <AuthContext.Provider value={contextValue}>
            {children}
        </AuthContext.Provider>
    )
}

AuthProvider.propTypes = {
    children: PropTypes.node.isRequired
}

export default AuthContext
